import React, { useState, useEffect } from "react";

import classnames from "classnames";
import { getRecommendationMode } from "QuotePage/app/compactQuotePage/Selectors";
import { CompareToggleButton } from "QuotePage/Components/Compare";
import { FavoriteToggleButton, isPlanFavorited } from "QuotePage/Components/FavoritePlan";
import { getCustomRecommendationByPlanId, getRecommendationIndexByPlanId, getEmployeeProfile } from "QuotePage/Selectors";
import { useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import simplifyPlanName from "Utils/simplifyPlanName";

import { Badge } from "Cobalt/Badge";
import { CarrierLogo } from "Cobalt/CarrierLogo";
import { Collapse } from "Cobalt/Collapse";
import { HighlightsTable, ServiceCostsTable } from "Cobalt/Plan";
import { RecommendedFor, FeatureBox, MonthlyCost, CollapseToggle } from "Cobalt/Plan/Card";
import Stack from "Cobalt/Stack";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import { isLoadingPlans } from "Common/PlanLoader";
import { getPlanById, getPlanDeviationProps, isPlanCompared } from "Common/PlanLoader/Selectors";
import { SizeSwitch } from "Common/Responsive";
import { Card, CardSection } from "Theme/Card";

import EmployeeCostsSection from "./planCard/EmployeeCostsSection";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

// Constants
const bem = makeBem("QuotePagePlanCard");
const ANALYTICS_LOCATION = "expanding_plan_card";

export type RecommendationType = "bestMatch" | "bestValue" | "moreDoctors" | "lowCopays";

export const REC_BADGES = {
  bestMatch: {
    label: "Best Match",
    color: "#F37336",
    icon: "trophy",
    explanation: "",
  },

  bestValue: {
    label: "Best Value",
    color: "#F37336",
    icon: "trophy",
    explanation: "This plan has the lowest monthly premiums. It has a high deductible, but provides catastrophic coverage and free preventative care.",
  },

  moreDoctors: {
    label: "More Doctors",
    color: "#1E3E72",
    icon: "hospital-user",
    explanation: "This plan is also a great value, but has access to a larger network of doctors. It's a good blend of affordability and coverage.",
  },

  lowCopays: {
    label: "Low Copays",
    color: "#f9c022",
    icon: "money-bill",
    explanation:
      "This plan has a low deductible and more coverage. It's good for people who use their insurance more often or prefer a higher level of benefits.",
  },
};

export type PlanCardProps = {
  planId: MedicalPlanPrice.id;
  plan?: MedicalPlanPrice;
  recommendationType?: RecommendationType;
  children?: any;
};

/**
 *
 * @param {string} planId - the id of the plan to display. Coupled to the PlanLoader logic ('allPlans' store slice).
 * @param {MedicalPlanPrice} plan - an optional plan object to use if planId doesn't match to a plan. Useful when your plan instance is not coupled to the PlanLoader.
 * @returns
 */
export const PlanCard = ({
  planId,
  plan: fallbackPlan,
  recommendationType,
  children,
  employeeCostsSectionComponent = <EmployeeCostsSection planId={planId} />,
  startExpanded = false,
  ...rest
}: PlanCardProps) => {
  const showMobileCompare = true;

  const loadingAllPlans = useSelector(isLoadingPlans);
  const plan = useSelector((state) => getPlanById(state, planId)) || fallbackPlan;
  const { average_monthly_premium: averageMonthlyPremium, prices_loading: isLoadingCosts } = plan;
  const isCompare = useSelector((state) => isPlanCompared(state, plan.id));
  const isFavorite = useSelector((state) => isPlanFavorited(state, plan.id));
  const recommendationMode = useSelector(getRecommendationMode);
  /** Custom reason text (e.g. "Leigh Says...") for recommending this plan */
  const customRecommendation = useSelector((state) => getCustomRecommendationByPlanId(state, plan.id));

  /** Index of this card in ordered sections */
  const recommendationIndex = useSelector((state) => getRecommendationIndexByPlanId(state, plan.id));

  /** If the card is expanded (true if it's the first recommended plan) */
  const [isExpanded, setIsExpanded] = useState(recommendationIndex === 0 || startExpanded);

  /** Auto-expand the first custom recommended plan after filling out survey */
  useEffect(() => {
    if (recommendationIndex === 0 && !!customRecommendation && !isExpanded) {
      setIsExpanded(true);
    }
  }, [recommendationIndex, customRecommendation]);

  const employeeProfile = useSelector(getEmployeeProfile);
  const zeroEmployeesWithAge = employeeProfile.every((e) => !e.age);

  const handleExpand = () => {
    const eventName = isExpanded ? "plan_card_collapse" : "plan_card_expand";
    AnalyticsTracking.trackEvent(eventName, {
      location: ANALYTICS_LOCATION,
      custom_recommendation: recommendationMode == "recommendation_mode",
    });

    setIsExpanded(!isExpanded);
  };

  const badge = recommendationType && REC_BADGES[recommendationType];
  const RecommendationBadge = () =>
    badge ? (
      <Badge
        color={badge.color}
        icon={badge.icon}
        tooltip={{
          id: `rec_badge_${plan.id}`,
          content: customRecommendation || badge.explanation,
          inverted: true,
        }}
      >
        {badge.label}
      </Badge>
    ) : null;

  const planName = simplifyPlanName(plan);
  const planDeviation = useSelector(getPlanDeviationProps);

  return (
    <Card
      data-testid="PlanCard"
      data-planid={planId}
      data-title={planName}
      theme="flat"
      {...rest}
      className={classnames(bem(), {
        [bem("Expanded")]: isExpanded,
      })}
    >
      <SizeSwitch
        xs={
          <>
            {/**
             * NOTE: Compare feature is not ready for mobile. CTA on screen bottom not developed yet.
             * Likely to be completed w/ new Favorite feature
             * @see https://simplyinsured.atlassian.net/browse/SI-15760?focusedCommentId=30263
             * */}
            {showMobileCompare && (
              <CardSection
                p={3}
                className={classnames(bem("Header"), {
                  [bem("Header", "Inverted")]: isCompare,
                })}
              >
                <FavoriteToggleButton planId={plan.id} checked={isFavorite} />
                <CompareToggleButton planId={plan.id} checked={isCompare} />
              </CardSection>
            )}

            <CardSection p={4} className={bem("Summary")}>
              <CarrierLogo plan={plan} style={{ width: "88px" }} />
              <RecommendationBadge />
              <MonthlyCost cost={averageMonthlyPremium} loading={Boolean(isLoadingCosts)} hasFootnote={zeroEmployeesWithAge} />
            </CardSection>
            <CardSection p={4} className={bem("Summary", "Features")}>
              <FeatureBox plan={plan} title="Tier" field="metal_tier" format="capitalize" />

              <FeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />

              <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
            </CardSection>
            <Div className={bem("PlanFooterActions")} p={3} pt={0}>
              {children}
            </Div>
          </>
        }
        sm={
          <>
            <CardSection data-testid="ExpandCard" className={bem("Summary")} onClick={handleExpand}>
              <Div mb={5}>
                <Stack direction="horizontal" justify="space-between" align="center">
                  <RecommendedFor plan={plan} isExpandable className="max-sm:hidden" />
                  <Stack direction="horizontal" gap="normal" align="center">
                    <FavoriteToggleButton planId={plan.id} checked={isFavorite} />
                    <CompareToggleButton planId={plan.id} checked={isCompare} />
                  </Stack>
                </Stack>
              </Div>
              <Stack direction="horizontal" justify="space-between" align="center">
                <Stack direction="horizontal" gap="spacious" align="center">
                  <CarrierLogo plan={plan} style={{ width: "88px" }} />
                  <FeatureBox plan={plan} title="Tier" field="metal_tier" format="capitalize" />
                  <FeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />
                  <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
                  <FeatureBox plan={plan} title="Doctor Visits" field="estimated_costs.office_visit" format="currency" className="sm:max-md:hidden" />
                  {badge && <RecommendationBadge />}
                </Stack>
                <Stack direction="horizontal" gap="condensed" align="center">
                  <MonthlyCost cost={averageMonthlyPremium} loading={Boolean(isLoadingCosts)} hasFootnote={zeroEmployeesWithAge} />
                  <CollapseToggle isExpanded={isExpanded} toggle={handleExpand} />
                </Stack>
              </Stack>
            </CardSection>
            <Collapse role="article" in={isExpanded} mountOnEnter>
              <CardSection>
                <Row>
                  <Col sm={6}>
                    <HighlightsTable plan={plan} />
                  </Col>
                  <Col sm={6}>{employeeCostsSectionComponent}</Col>
                  <Col mt={4}>
                    <ServiceCostsTable plan={plan} planDeviation={planDeviation} loading={loadingAllPlans} />
                  </Col>
                </Row>
                <Div pt={4}>{children}</Div>
              </CardSection>
            </Collapse>
          </>
        }
      />
    </Card>
  );
};

export default PlanCard;
